import {
  LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { DEF } from '../constants/defaults';
import { FF } from '../utils/format';
import { Card } from '../components/Card';
import { Heading } from '../components/Heading';
import { Slider } from '../components/Slider';
import { Toggle } from '../components/Toggle';
import { NumInput } from '../components/NumInput';
import { ProgressBar } from '../components/ProgressBar';
import { Grid } from '../components/Grid';
import { Stack } from '../components/Stack';
import { Wallet, CreditCard, DollarSign, Shield, Target, ShieldCheck, PiggyBank } from '../components/icons';
import { useApp } from '../hooks/useApp';
import InsightBanner from '../features/motivation/InsightBanner';
import { budgetInsights } from '../features/motivation/motivationEngine';
import SankeyFlow from '../features/sankey/SankeyFlow';

export default function Budget() {
  const { s, c, proj, pF, p0, up, upM, setS, tt, fiAge, fiReached, coastAge, budgetMonthly, blendedReturn, realReturn, amor, scen, mcData, mcRunning, runMonteCarlo, showToast, setConfirm, fileRef } = useApp();
  const cats = s.cats || DEF.cats;
  const tB = cats.reduce((a, x) => a + x.b, 0);
  const tA = cats.reduce((a, x) => a + x.a, 0);
  const util = s.ccLim > 0 ? (s.ccBal / s.ccLim) * 100 : 0;
  const net = s.cheq - s.ccBal;
  const sCol = s.cScore >= 750 ? c.green : s.cScore >= 700 ? c.blue : s.cScore >= 650 ? c.orange : c.red;
  const sLab = s.cScore >= 750 ? "Excellent" : s.cScore >= 700 ? "Good" : s.cScore >= 650 ? "Fair" : "Build";
  const uCat = (id, f, v) => up("cats", cats.map((x) => (x.id === id ? { ...x, [f]: v } : x)));
  const efTarget = (s.efTarget || 6) * tA;
  const efCurrent = s.cheq;
  const efProg = efTarget > 0 ? Math.min(100, (efCurrent / efTarget) * 100) : 0;
  const insights = budgetInsights(s);

  return (
    <Stack>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        {[
          { l: "Chequing", v: FF(s.cheq), ic: Wallet, cl: c.blue },
          { l: "Credit Used", v: FF(s.ccBal), ic: CreditCard, cl: c.red, sub: `${util.toFixed(0)}% utilization` },
          { l: "Net Cash", v: FF(net), ic: DollarSign, cl: net >= 0 ? c.green : c.red },
          { l: "Credit Score", v: s.cScore, ic: Shield, cl: sCol, sub: sLab },
        ].map((x, i) => (
          <Card key={x.l} c={c} delay={i * 0.04} sx={{ padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 8 }}>
              <x.ic size={14} style={{ color: x.cl }} />
              <span style={{ fontSize: 11, color: c.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>{x.l}</span>
            </div>
            <div style={{ fontSize: 26, fontWeight: 700, color: x.cl, letterSpacing: "-0.02em" }}>{x.v}</div>
            {x.sub && <div style={{ fontSize: 12, color: c.muted, marginTop: 3 }}>{x.sub}</div>}
          </Card>
        ))}
      </div>

      <InsightBanner insights={insights} />

      <SankeyFlow />

      <Grid>
        <Card c={c} delay={0.08}>
          <Heading ic={Wallet} c={c}>Accounts</Heading>
          <NumInput l="Chequing" v={s.cheq} fn={(v) => up("cheq", v)} c={c} />
          <div style={{ height: 14 }} />
          <NumInput l="Credit Balance" v={s.ccBal} fn={(v) => up("ccBal", v)} c={c} />
          <div style={{ height: 14 }} />
          <NumInput l="Credit Limit" v={s.ccLim} fn={(v) => up("ccLim", v)} c={c} />
          <div style={{ height: 14 }} />
          <Slider l="Credit Score" v={s.cScore} fn={(v) => up("cScore", v)} min={300} max={900} c={c} />
        </Card>
        <Card c={c} delay={0.12}>
          <Heading ic={Target} c={c}>Score Trend</Heading>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={s.csHist || DEF.csHist}>
              <CartesianGrid strokeDasharray="3 3" stroke={c.border} />
              <XAxis dataKey="m" tick={{ fontSize: 12, fill: c.muted }} axisLine={{ stroke: c.border }} tickLine={false} />
              <YAxis domain={["auto", "auto"]} tick={{ fontSize: 12, fill: c.muted }} axisLine={false} tickLine={false} />
              <Tooltip {...tt} />
              <Line type="monotone" dataKey="s" stroke={c.blue} strokeWidth={2.5} dot={{ fill: c.blue, r: 4, strokeWidth: 0 }} name="Score" />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </Grid>

      <Card c={c} delay={0.13}>
        <Heading ic={ShieldCheck} c={c}>Emergency Fund</Heading>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div>
            <span style={{ fontSize: 24, fontWeight: 700, color: efProg >= 100 ? c.green : c.orange }}>{FF(efCurrent)}</span>
            <span style={{ fontSize: 13, color: c.muted }}> / {FF(efTarget)} target</span>
          </div>
          <div style={{ padding: "4px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600,
            background: efProg >= 100 ? (s.dark ? "rgba(48,209,88,0.1)" : "rgba(48,209,88,0.06)") : (s.dark ? "rgba(255,159,10,0.1)" : "rgba(255,159,10,0.06)"),
            color: efProg >= 100 ? c.green : c.orange,
          }}>
            {efProg >= 100 ? "Fully funded" : `${Math.round(efProg)}% funded`}
          </div>
        </div>
        <ProgressBar v={efCurrent} mx={efTarget} l={`${s.efTarget || 6} months of expenses`} color={efProg >= 100 ? c.green : c.orange} c={c} />
        <Slider l="Target Months" v={s.efTarget || 6} fn={(v) => up("efTarget", v)} min={3} max={12} c={c} />
      </Card>

      <Card c={c} delay={0.14} sx={{ padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: c.fg }}>
            Budget → Projection Sync {s.budgetSync ? "Active" : "Off"}
          </div>
          <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>
            {s.budgetSync
              ? `Projections use ${FF(tA * 12)}/yr from your budget categories`
              : `Projections use fixed ${FF(s.liv)}/yr — budget changes won't affect FI date`
            }
          </div>
        </div>
        <Toggle l="" on={s.budgetSync} fn={(v) => up("budgetSync", v)} c={c} />
      </Card>

      <Card c={c} delay={0.15}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
          <Heading ic={PiggyBank} c={c}>Monthly Expenses</Heading>
          <div style={{
            fontSize: 13, fontWeight: 700,
            color: tA <= tB ? c.green : c.red,
            padding: "4px 12px", borderRadius: 20,
            background: tA <= tB
              ? (s.dark ? "rgba(48,209,88,0.1)" : "rgba(48,209,88,0.06)")
              : (s.dark ? "rgba(255,55,95,0.1)" : "rgba(255,55,95,0.06)"),
          }}>
            {FF(tA)} / {FF(tB)} · {tA <= tB ? `${FF(tB - tA)} under` : `${FF(tA - tB)} over`}
          </div>
        </div>
        {cats.map((cat) => (
          <div key={cat.id} style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ width: 8, height: 8, borderRadius: 4, background: cat.c }} />
                <span style={{ fontSize: 13, fontWeight: 500, color: c.fg }}>{cat.n}</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input type="number" value={cat.a}
                  onChange={(e) => uCat(cat.id, "a", Number(e.target.value) || 0)}
                  style={{
                    width: 70, padding: "5px 8px", borderRadius: 8,
                    border: `1px solid ${c.border}`, background: c.inputBg,
                    color: c.fg, fontSize: 13, fontWeight: 600,
                    textAlign: "right", outline: "none", fontVariantNumeric: "tabular-nums",
                  }} />
                <span style={{ fontSize: 12, color: c.muted, minWidth: 55, textAlign: "right" }}>/ {FF(cat.b)}</span>
              </div>
            </div>
            <div style={{ height: 4, borderRadius: 2, background: c.border, overflow: "hidden" }}>
              <div style={{
                height: "100%", borderRadius: 2,
                background: cat.a > cat.b ? c.red : cat.c,
                width: `${Math.min(100, (cat.a / Math.max(1, cat.b)) * 100)}%`,
                transition: "width 0.4s cubic-bezier(0.25,0.1,0.25,1)",
              }} />
            </div>
          </div>
        ))}
      </Card>
    </Stack>
  );
}
